import React from 'react'
import { useQuery } from '@tanstack/react-query'
import VouchersAPI from 'api/faber/VouchersAPI'
import SimOpsAPI from 'api/ops/v2/SimOpsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import addMonths from 'date-fns/addMonths'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import { ServiceCatalogItem } from 'types/api/ops/v2/simOps.type'

import {
    CellText,
    fitTableHeight,
    listGridProps,
    MonoText,
    MutedText,
    StatusPill
} from './listUi'

// lounge pass / fast track vouchers are valid for 3 months from purchase
const SERVICE_VOUCHER_VALIDITY_MONTHS = 3

const formatDate = (value?: string) =>
    value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'

// maps an ops service catalog item (lounge pass / fast track) onto the voucher table row shape
const toVoucherRow = (item: ServiceCatalogItem) => ({
    ID: item.id,
    code: item.voucher_code,
    name: item.catalog_name || item.catalog_id,
    issued_at: item.created_at,
    expiry_at: item.created_at
        ? addMonths(new Date(item.created_at), SERVICE_VOUCHER_VALIDITY_MONTHS).toISOString()
        : '',
    voucher_type: item.type
})

type VoucherRow = {
    ID: string | number
    code: string
    issued_at?: string | null
    [key: string]: unknown
}

const hasIssuedAt = (voucher: VoucherRow) => {
    if (!voucher.issued_at) return false

    const issuedAt = new Date(voucher.issued_at)
    return !Number.isNaN(issuedAt.getTime()) && issuedAt.getUTCFullYear() > 1
}

export const mergeVoucherResponses = (
    oldVouchers: VoucherRow[],
    serviceVouchers: VoucherRow[]
) => {
    const vouchersByCode = new Map<string, VoucherRow>()

    ;[...oldVouchers, ...serviceVouchers].forEach(voucher => {
        const existingVoucher = vouchersByCode.get(voucher.code)

        if (
            !existingVoucher ||
            !hasIssuedAt(existingVoucher) ||
            hasIssuedAt(voucher)
        ) {
            vouchersByCode.set(voucher.code, voucher)
        }
    })

    return Array.from(vouchersByCode.values()).sort((left, right) =>
        (hasIssuedAt(right) ? new Date(right.issued_at as string).getTime() : 0) -
        (hasIssuedAt(left) ? new Date(left.issued_at as string).getTime() : 0)
    )
}

const voucherColumns = [
    {
        field: 'name',
        headerName: 'Reward',
        sortable: false,
        flex: 1.6,
        minWidth: 220,
        renderCell: ({ row }) => <CellText strong>{row.name}</CellText>
    },
    {
        field: 'code',
        headerName: 'Code',
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }) => <MonoText>{row.code}</MonoText>
    },
    {
        field: 'voucher_type',
        headerName: 'Type',
        sortable: false,
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
            <StatusPill status={row.voucher_type} tone='neutral' />
        )
    },
    {
        field: 'issued_at',
        headerName: 'Issued',
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ row }) => <MutedText>{formatDate(row.issued_at)}</MutedText>
    },
    {
        field: 'expiry_at',
        headerName: 'Expires',
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ row }) => <MutedText>{formatDate(row.expiry_at)}</MutedText>
    }
]

function JetpassVoucherTable({ userId, isExternal }: { userId: string, isExternal: boolean }) {
    const faberToken = OrbitSession.get('faberToken')

    const { data: allVouchers } = useQuery(
        ['userVoucherData', userId],
        async () => {
            const oldVoucherResponse = isExternal ?
                VouchersAPI.getExternalAllVoucher(
                    faberToken,
                    userId
                )
                :
                VouchersAPI.getAllVoucher(
                    faberToken,
                    userId
                )

            // internal users read lounge pass / fast track from the ops
            // service catalog items endpoint; limit 0 returns all of them
            const serviceVoucherResponse = isExternal
                ? VouchersAPI.getUserServiceVouchers(faberToken, userId).then(
                    (res) => res?.data ?? []
                )
                : SimOpsAPI.getUserServiceCatalogItems({
                    userId,
                    limit: 0,
                    offset: 0
                }).then(({ data }) => (data?.body?.data ?? []).map(toVoucherRow))

            const [oldVouchers, serviceVouchers] = await Promise.all([
                oldVoucherResponse,
                serviceVoucherResponse
            ])

            return mergeVoucherResponses(
                oldVouchers?.body?.data ?? [],
                serviceVouchers
            )
        }
    )

    return (
        <MUIDataGrid
            {...listGridProps}
            columns={voucherColumns}
            rows={allVouchers ?? []}
            getRowId={(row) => row.code}
            hideFooter
            tableHeight={fitTableHeight(allVouchers?.length ?? 0)}
        />
    )
}

export default JetpassVoucherTable
